import { useCallback, useEffect, useState } from 'react';
import { BookOpen, User } from 'lucide-react';
import { Button } from '@ludolio/shared-ui';
import { useLabsReview } from './context';
import { LabsReviewDetail } from './LabsReviewDetail';
import { formatDate } from './format';
import {
  ListCell,
  MetaItem,
  PlatformChips,
  ProjectHeading,
  ProjectList,
  ProjectRow,
  PUBLISHED_LIST_COLUMNS,
  REVIEW_LIST_COLUMNS,
  RowActions,
  VisibilityCell,
} from './ProjectRow';
import type { LabReviewQueueItem, LabReviewScope } from './types';

interface LabsReviewQueueProps {
  scope: LabReviewScope;
  unitId?: string;
  emptyMessage: string;
  onDecided?: () => void;
  alignWithPublished?: boolean;
}

export function LabsReviewQueue({ scope, unitId, emptyMessage, onDecided, alignWithPublished }: LabsReviewQueueProps) {
  const { api, getErrorMessage } = useLabsReview();
  const columns = alignWithPublished ? PUBLISHED_LIST_COLUMNS : REVIEW_LIST_COLUMNS;
  const [items, setItems] = useState<LabReviewQueueItem[] | null>(null);
  const [error, setError] = useState('');
  const [openReleaseId, setOpenReleaseId] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listQueue(scope, unitId)
      .then((result) => {
        setItems(result ?? []);
        setError('');
      })
      .catch((err) => setError(getErrorMessage(err)));
  }, [api, getErrorMessage, scope, unitId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!items && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {items?.length === 0 && (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">{emptyMessage}</div>
      )}
      {items && items.length > 0 && (
        <ProjectList columns={columns} headings={['Project', 'Who can play', 'Submitted']}>
          {items.map((item) => (
            <ProjectRow key={item.releaseId} columns={columns}>
              <ProjectHeading title={item.title} version={item.version} iconUrl={item.iconUrl}>
                {item.unit && !unitId && <MetaItem icon={BookOpen} label="Unit"><span className="font-medium">{item.unit.code}</span></MetaItem>}
                {item.submittedBy && <MetaItem icon={User} label="Submitted by">{item.submittedBy}</MetaItem>}
                <PlatformChips platforms={item.platforms} />
              </ProjectHeading>
              <VisibilityCell visibility={item.visibility} />
              <ListCell label="Submitted">{formatDate(item.submittedAt)}</ListCell>
              <RowActions>
                <Button size="sm" variant="outline" onClick={() => setOpenReleaseId(item.releaseId)}>Review</Button>
              </RowActions>
            </ProjectRow>
          ))}
        </ProjectList>
      )}
      <LabsReviewDetail
        releaseId={openReleaseId}
        canReadResponses={scope === 'organization'}
        onClose={() => setOpenReleaseId(null)}
        onDecided={() => {
          load();
          onDecided?.();
        }}
      />
    </>
  );
}
